import React, { useState } from "react";
import { supabase } from "../../../services/supabaseClient";

type SearchFactoriesModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSelectFactory: (factory: any) => void; // ✅ new prop
};

const SearchFactoriesModal = ({ isOpen, onClose, onSelectFactory }: SearchFactoriesModalProps) => {
  const [results, setResults] = useState(() => []);
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const handleSearch = async () => {
    setLoading(true);
    try {
      // ✅ Query factories table (user-owned only)
      let query = supabase
        .from("factories")
        .select("id, name, address_line_1, city, country, state_province, postal_code")
        .order("name");

      if (name.trim()) {
        query = query.ilike("name", `%${name.trim()}%`);
      }

      if (address.trim()) {
        query = query.or(
          `address_line_1.ilike.%${address.trim()}%, city.ilike.%${address.trim()}%, country.ilike.%${address.trim()}%`
        );
      }

      const { data, error } = await query.limit(50);

      if (error) {
        console.error("Factories search error:", error);
        setResults([]);
      } else {
        setResults(data || []);
      }
    } catch (err) {
      console.error("Unexpected search error:", err);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="data-modal-overlay" onClick={onClose}>
      <div className="data-modal" onClick={(e) => e.stopPropagation()}>
        <div className="data-modal-header">
          <h2 className="data-modal-title">Search Factories</h2>
          <button className="modal-close-btn" onClick={onClose}>
            ×
          </button>
        </div>

        {/* Modal Body */}
        <div className="data-modal-body">
          <div className="form-grid">
            {/* Factory Name */}
            <div className="form-group">
              <label className="form-label">Factory Name</label>
              <input
                type="text"
                className="form-input"
                placeholder="Search by factory name..."
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>

            {/* Factory Address */}
            <div className="form-group">
              <label className="form-label">Factory Address</label>
              <input
                type="text"
                className="form-input"
                placeholder="Search by address (line, city, country)..."
                value={address}
                onChange={(e) => setAddress(e.target.value)}
              />
            </div>

            {/* Search Button */}
            <div className="form-group">
              <button
                className="modal-btn modal-btn-save"
                onClick={handleSearch}
                disabled={loading}
              >
                {loading ? "Searching..." : "Search"}
              </button>
            </div>
          </div>

          {/* Instruction Line */}
          <div
            style={{
              fontSize: "0.9em",
              margin: "10px 0 6px 0",
              color: "#444",
            }}
          >
            Click <strong>Use This Factory</strong> to select and confirm the factory that produced the product(s).
          </div>

          {/* Results */}
          <div
            className="results-list"
            style={{
              maxHeight: "250px",
              overflowY: "auto",
              marginTop: "5px",
              border: "1px solid #ccc",
              borderRadius: "4px",
              padding: "8px",
            }}
          >
            {results.length === 0 ? (
              <div>No factories found</div>
            ) : (
              results.map((factory: any) => (
                <div
                  key={`factory-${factory.id}`}
                  className="factory-result"
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "4px",
                    padding: "8px 0",
                    borderBottom: "1px solid #eee",
                  }}
                >
                  <div>
                    <strong>{factory.name}</strong>
                    <div style={{ fontSize: "0.9em", color: "#555" }}>
                      {factory.address_line_1}{" "}
                      {factory.city ? `, ${factory.city}` : ""}{" "}
                      {factory.country ? `, ${factory.country}` : ""}
                    </div>
                  </div>

                  {/* Use This Factory Button */}
                  <button
                    className="modal-btn modal-btn-save"
                    style={{
                      alignSelf: "flex-start",
                      fontSize: "0.8em",
                      padding: "4px 10px",
                      marginTop: "2px",
                    }}
                    onClick={() => {
                      onSelectFactory(factory);
                      onClose();
                    }}
                  >
                    Use This Factory
                  </button>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="data-modal-footer">
          <button className="modal-btn modal-btn-cancel" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default SearchFactoriesModal;
